import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero, Breadcrumbs, TestimonialBand, FadeIn } from "@/components/Shared";

const CASE_SECTIONS = [
  {
    title: "Violence",
    cases: [
      "R v L: multi-handed section 18 GBH involving fingerprint, DNA and identification evidence.",
      "R v L: firearm offences in the Magistrates' Court, resulting in an exceptional conditional discharge.",
      "R v O: large violent disorder outside a residential premises where mitigation secured a Community Order.",
    ],
  },
  {
    title: "Sexual Offences",
    cases: [
      "R v A: acquittal of an individual charged with alleged date rape after trial at Inner London Crown Court.",
      "R v I: one night stand rape prosecution at Maidstone Crown Court.",
      "R v K: historic sexual offending appeal fast-tracked and allowed in the Court of Appeal.",
      "R v M: representation of one of multiple siblings accused of historic rape allegations.",
    ],
  },
  {
    title: "Drugs",
    cases: [
      "R v S: defence in the largest prosecution brought for importation and production of anabolic steroids.",
      "R v K: cryptocurrency, DarkWeb and fake Xanax tablets at Portsmouth Crown Court.",
      "EncroChat: represented multiple individuals charged with wide-scale cocaine supply.",
      "R v L: one tonne of cocaine imported to Europe and infiltrated into the UK market.",
      "R v H and R v U: industrial-level cannabis production and cocaine importation allegations.",
    ],
  },
  {
    title: "Murder",
    cases: [
      "R v H: four-handed murder trial at the Central Criminal Court involving zombie knives purchased on Snapchat.",
      "R v L: gang murder trial at Oxford Crown Court.",
      "R v E and R v K: Central Criminal Court honey trap trial and murder by stamping allegation.",
      "R v N: murder allegation involving more than 30 hammer blows to the head.",
    ],
  },
  {
    title: "Fraud",
    cases: [
      "R v M: alleged fraud of a solicitor's client account due to be tried at Southwark Crown Court.",
      "Operation Barbados: cheating the public revenue, MTIC.",
      "T v T: successful defence of a businessman against a private prosecution arising from international commercial litigation.",
      "AM v SFO: conviction overturned for failing to comply with a section 2 notice in the ENRC investigation.",
      "Operation Cotton: conspiracy to defraud allegation in a major land-banking prosecution.",
    ],
  },
  {
    title: "Corporate Offences",
    cases: [
      "R v W: acquittal of an individual charged with bribery and money laundering after trial at Southwark Crown Court.",
      "R v L: fine achieved for bribery charge for a corporate entity and primary director after successful negotiation.",
      "R v X: advice to company directors in a bribery and corruption investigation.",
      "R v K: Insolvency Service prosecution where large-scale confiscation proceedings were defeated entirely.",
      "Trading Standards investigations involving alleged substandard and unnecessary building works.",
    ],
  },
  {
    title: "Health & Safety",
    cases: [
      "Grenfell Tower Inquiry.",
      "R v M: building contractor prosecuted for health and safety breaches following a gas leak.",
    ],
  },
  {
    title: "Professional Discipline",
    cases: [
      "General Medical Council.",
      "Nursing and Midwifery Council.",
      "Health and Care Professions Council.",
      "General Dental Council.",
    ],
  },
  {
    title: "Motoring",
    cases: [
      "Dangerous driving causing widespread destruction to public highways and buildings.",
      "Careless driving.",
      "Excess alcohol.",
      "Drug driving.",
      "Speeding.",
    ],
  },
  {
    title: "General Crime",
    cases: [
      "Blackmail and false imprisonment.",
      "Multiple controlling and coercive behaviour, stalking and harassment cases.",
      "Affray, violent disorder and riot, including football violence.",
    ],
  },
  {
    title: "Inquests and Public Inquiries",
    cases: [
      "Grenfell Tower Inquiry.",
      "Stephen Port Inquest.",
      "Jean Charles de Menezes.",
      "Mark Duggan.",
    ],
  },
  {
    title: "Extradition",
    cases: [
      "Requests involving the USA, Rwanda, Germany, India, Turkiye, Spain and Poland.",
      "Multiple other European jurisdictions.",
    ],
  },
];

export default function NotableCases() {
  return (
    <main data-testid="notable-cases-page">
      <PageHero
        eyebrow="Notable cases"
        title="Serious cases. Careful preparation. Determined defence."
        lead="Examples of ways our criminal law experts have helped clients across high-profile extradition cases, murder, fraud, violence, sexual offences, regulatory matters and public inquiries."
      >
        <Link
          to="/contact"
          className="brand-cta-primary mt-8 inline-flex px-8 py-4 tracking-[0.2em]"
          data-testid="notable-cases-hero-cta"
        >
          Get in touch for expert legal advice
        </Link>
      </PageHero>
      <Breadcrumbs items={[{ label: "Notable Cases" }]} />

      <section className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="brand-container">
          <FadeIn>
            <div className="max-w-3xl border-l-2 border-brand-gold pl-6">
              <p className="leading-relaxed text-slate-600">
                For over 70 years, Hallinans have represented individuals and
                organisations facing some of the most serious and complex criminal
                investigations and prosecutions.
              </p>
              <p className="mt-4 leading-relaxed text-slate-600">
                Much of our work remains necessarily confidential. These examples
                illustrate the breadth of our expertise and the care, diligence and
                determination we bring to every client&apos;s defence.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-slate-500">
                The result achieved in any individual case depends on its specific
                facts and circumstances.
              </p>
            </div>
          </FadeIn>

          <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {CASE_SECTIONS.map((section, i) => (
              <FadeIn key={section.title} delay={(i % 2) * 0.08}>
                <article
                  className="brand-card flex h-full flex-col p-7"
                  data-testid={`notable-case-section-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-terracotta">
                    Case type
                  </p>
                  <h2 className="brand-heading mt-3 text-2xl">
                    {section.title}
                  </h2>
                  <ul className="mt-5 space-y-3">
                    {section.cases.map((item) => (
                      <li key={item} className="flex gap-3 text-sm leading-relaxed text-slate-600">
                        <span className="mt-2 size-1.5 shrink-0 bg-brand-gold" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.2}>
            <div className="mt-12 flex flex-col items-start justify-between gap-6 border-t border-slate-200 pt-8 sm:flex-row sm:items-center">
              <p className="max-w-xl text-sm leading-relaxed text-slate-500">
                Need representation in a serious or complex matter? Speak to the team
                before responding to investigators or attending an interview.
              </p>
              <Link
                to="/contact"
                className="brand-text-link flex shrink-0 items-center gap-2"
                data-testid="notable-cases-contact-link"
              >
                Contact us <ArrowRight className="size-3.5" />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <TestimonialBand />
    </main>
  );
}
